/**
### Overview

This service backs all `location-selector` components to ensure that selecting
a location is based on accurate data - only displaying authorized units,
reflecting any changes made to those units during the session, and sorting those
units consistently across pickers.

The recommend way to use this service is to call the `configure` method in the model
hook of any route that will use the `location-selector` component. The component
will handle most interaction of getting/setting location data on this service, but also
exposes a `change` action in case your controller needs to be notified of any updates.
*/

import { isNone } from '@ember/utils';
import { assert } from '@ember/debug';
import { readOnly, mapBy } from '@ember/object/computed';
import Service, { inject as service } from '@ember/service';
import { computed, set } from '@ember/object';
import { sortUnits } from '@dashboard/junk-drawer/utils/subunits';
import { locationsSelected } from '@dashboard/junk-drawer/events/filters/event-catalog';
import { intersection } from '@square/dashboard-utilities/utils/intersection';

/**
 * Select a single unit from the list of units.
 * Defaults to selecting the first unit.
 * Can be overridden to select the unit that has a given unit token.
 *
 * @param   {Array<MerchantSubunit>} units Array of unit objects to select units from.
 * @param   {Array<string>} [tokens] Array of string token values. Will select unit that matches token, if exists.
 * @returns {Array<MerchantSubunit>}
 */
function selectSingleToken(units, tokens) {
  let selectedUnit = units[0];
  if (tokens) {
    selectedUnit = units.findBy('token', tokens[0]) || selectedUnit;
  }
  return selectedUnit ? [selectedUnit] : [];
}

/**
 * Select multiple tokens from the list of units.
 * Defaults to selecting all units.
 * Can be overridden to selecting any units which match given tokens.
 *
 * @param   {Array<MerchantSubunit>} units Array of unit objects to select units from.
 * @param   {Array<string>} [tokens] Array of string token values. Will select any units that match tokens, if exist.
 * @param   {boolean} showInactiveUnitsByDefault Indicates whether to select inactive units by default on load. Defaults to false.
 * @returns {Array<MerchantSubunit>}
 */
function selectMultipleTokens(units, tokens, showInactiveUnitsByDefault = false) {
  // Select only active units by default
  // However, if all units are inactive or if showInactiveUnitsByDefault is set, then select all
  const activeUnits = showInactiveUnitsByDefault ? units : units.filterBy('unitActive');
  const defaultSelection = activeUnits.length > 0 ? activeUnits : units;
  let selectedUnits = defaultSelection;
  if (tokens) {
    selectedUnits = units.filter(unit => tokens.includes(unit.get('token')));
    if (selectedUnits.length === 0) {
      selectedUnits = defaultSelection;
    }
  }
  return selectedUnits;
}

/**
 * Generate a computed property that sorts the units in the given key.
 *
 * @param   {string} unitsKey Property to sort. Should contain an array of MerchantSubunits.
 * @returns {Function}
 */
function sorted(unitsKey) {
  const sortedKey = `_sorted_${unitsKey}`;
  return computed(`${unitsKey}.@each.{nickname,unitActive}`, sortedKey, {
    get() {
      if (this[sortedKey] !== undefined) {
        return this[sortedKey];
      }
      return [...this.get(unitsKey)].sort(sortUnits);
    },
    set(_, value) {
      return set(this, sortedKey, value);
    }
  });
}

// eslint-disable-next-line ember/no-classic-classes
export default Service.extend({
  eventstream: service(),
  merchantSubunitStore: service(),
  store: service(),
  userUnits: service(),
  userPermissions: service(),
  isLoading: true,
  multiple: true,
  hideInactiveUnits: false,
  // set up in #configure
  shownUnits: null,
  hideIfOneUnit: true,
  /* Canonical list of all authorized units available in this session. */
  _authorizedUnits: null,
  /* List of units that can be selected. */
  units: computed('_authorizedUnits.@each.unitActive', '_units', 'hideInactiveUnits', {
    get() {
      if (this._units !== undefined) {
        return this._units;
      }
      if (this.hideInactiveUnits) {
        return this._authorizedUnits.filterBy('unitActive');
      }
      return this._authorizedUnits;
    },
    set(_, value) {
      return set(this, '_units', value);
    }
  }),
  selectedUnits: null,
  init(...args) {
    this._super(...args);
    if (!this.selectedUnits) {
      this.set('selectedUnits', []);
    }
    if (!this._authorizedUnits) {
      this.set('_authorizedUnits', []);
    }
  },
  sortedUnits: sorted('units'),
  sortedSelectedUnits: sorted('selectedUnits'),
  firstSelectedUnit: readOnly('sortedSelectedUnits.firstObject'),
  selectedUnitTokens: mapBy('selectedUnits', 'token'),
  allActiveUnitsSelected: computed('_authorizedUnits.@each.unitActive', 'selectedUnits.@each.unitActive', function () {
    const activeUnits = this._authorizedUnits.filterBy('unitActive');
    const selectedActiveUnits = this.selectedUnits.filterBy('unitActive');
    return activeUnits.get('length') === selectedActiveUnits.get('length');
  }),
  allUnitsSelected: computed('units.length', 'selectedUnits.length', function () {
    return this.units?.length === this.selectedUnits?.length;
  }),
  noUnitsSelected: computed('_noUnitsSelected', 'multiple', 'selectedUnits.length', 'shouldShowSelector', {
    get() {
      if (this._noUnitsSelected !== undefined) {
        return this._noUnitsSelected;
      }
      return this.shouldShowSelector && this.multiple && this.selectedUnits?.length === 0;
    },
    set(_, value) {
      return set(this, '_noUnitsSelected', value);
    }
  }),
  /* Only show the selector if there's more than one unit to select from. */
  shouldShowSelector: computed('units.length', 'hideIfOneUnit', function () {
    // Don't show selector if merchant only has access to one unit and hideIfOneUnit is true
    return this.units?.length > 1 || !this.hideIfOneUnit;
  }),
  /**
   * Configure state of service, load units, and set initial selection.
   * Should be called before attempting to render a `location-selector` component.
   * Resolves with array of initially selected units.
   *
   * @param   {Array<string>} [options.tokens]       Array of string token values to select. If `single` is true, must have single element.
   * @param   {boolean}       [options.single=false] Indicates whether to force single selection. Defaults to false.
   * @param   {boolean}       [options.hideInactiveUnits=false] Indicates whether to hide inactive units. Defaults to false.
   * @param   {boolean}       [options.showInactiveUnitsByDefault=false] Indicates whether to select inactive units by default on first load. Defaults to false.
   * @param   {Array<string>} [options.shownUnits] Array of unit tokens to display in the dropdown. If null, shows all units the current user has access to.
   * @param   {boolean}       [options.hideIfOneUnit=true] Indicates whether to hide the location selector if there is only one unit. Defaults to true.
   * @returns {Promise<Array<MerchantSubunit>>}
   */
  configure({
    tokens,
    single = false,
    hideInactiveUnits = false,
    showInactiveUnitsByDefault = false,
    shownUnits = null,
    hideIfOneUnit = true
  } = {}) {
    if (tokens) {
      assert('`tokens` must be an array', Array.isArray(tokens));
      tokens.forEach(function (token) {
        assert('`tokens` must be an array of strings', typeof token === 'string');
      });
      if (single) {
        assert('if `single` is true, `tokens` must have a single element', tokens.length === 1);
      }
    }
    this.setProperties({
      isLoading: true,
      selectedUnits: [],
      hideInactiveUnits,
      shownUnits,
      hideIfOneUnit
    });
    return this.loadUnits().then(() => {
      const multiple = single === false;
      const selectedUnits = multiple ? selectMultipleTokens(this.sortedUnits, tokens, showInactiveUnitsByDefault) : selectSingleToken(this.sortedUnits, tokens);
      this.setProperties({
        isLoading: false,
        multiple,
        selectedUnits
      });
      return selectedUnits;
    });
  },
  /**
   * Load all authorized units and updates service state.
   * No-ops if no changes made to units during this session.
   *
   * @returns {Promise<Array<MerchantSubunit>>}
   */
  async loadUnits() {
    try {
      const authorizedUnits = await this.merchantSubunitStore.findAuthorized();
      const authorizedUnitsToShow = this.shownUnits ? authorizedUnits.filter(unit => this.shownUnits.includes(unit.token)) : authorizedUnits;
      this.set('_authorizedUnits', authorizedUnitsToShow);
      return authorizedUnitsToShow;
    } finally {
      this.set('isLoading', false);
    }
  },
  /**
   * WFE-1569 Track inactive locations as part of A/B test.
   * This function (and its usages) should be removed when a decision has been made.
   *
   * As a note, this function always loads units (a memoized promise) in order to
   * ensure that correctly formatted data is being tracked. Some old usages of sq-subunits-filter
   * use the objects off `currentUser.subunits`, which don't include active status. If a call
   * has already been made to fetch the authorized units, the promise to `loadUnits` will resolve
   * automatically without making additional server calls.
   *
   * @param   {Array<MerchantSubunit>}   options.selectedUnits       Units that were selected.
   * @param   {Boolean}                  options.isNewSelector       Set to false if using old selector (sq-subunits-filter).
   * @param   {Boolean}                  options.isMultipleSelection Set to false if using single selection mode.
   * @param   {Boolean}                  options.isDefaultSelection  Set to false if user has modified this selection.
   * @returns {Promise}
   */
  trackInactiveSelections({
    selectedUnits = [],
    isNewSelector,
    isMultipleSelection,
    isDefaultSelection
  } = {}) {
    if (isNewSelector === undefined || isMultipleSelection === undefined || isDefaultSelection === undefined) {
      throw new Error('Caller must specify all arguments for trackInactiveSelections');
    }
    return this.loadUnits().then(units => {
      const filteredSelectedUnits = units.filter(unit => selectedUnits.findBy('token', unit.get('token')));
      const inactiveUnits = units.filterBy('unitActive', false);
      const inactiveSelectedUnits = filteredSelectedUnits.filterBy('unitActive', false);
      const metadata = {
        unitCount: units.get('length'),
        inactiveUnitCount: inactiveUnits.get('length'),
        selectedUnitCount: filteredSelectedUnits.get('length'),
        selectedInactiveUnitCount: inactiveSelectedUnits.get('length'),
        isMultipleSelection,
        isDefaultSelection,
        isNewSelector
      };
      this.eventstream.track(locationsSelected(metadata));
    });
  },
  // This function can filter two kinds of object lists, either each object has one subunit property specifying the location of that object,
  // or it can have a subunits property with an array of subunits that contains that object. Either way the returning array is filtered to just
  // objects that are located in the filtered set of locations.
  filterBySelectedUnits(objectsToFilter) {
    if (!objectsToFilter) {
      return [];
    }

    // If filter is set to all subunits and the current user has access to all subunits then there is nothing to filter.
    // (asilverman, TODO): remove the hasAccessToAllSubunits check once the server properly returns only items
    // the employee has access to.
    if (this.allUnitsSelected && this.userUnits.hasAccessToAllSubunits) {
      return objectsToFilter;
    }
    const selectedUnitTokens = this.selectedUnits.mapBy('token');

    // Does this object have multiple subunits?
    if (!isNone(objectsToFilter.firstObject?.subunits)) {
      const filteredObjects = [];
      objectsToFilter.forEach(object => {
        if (intersection(object.subunits.mapBy('token'), selectedUnitTokens).length > 0) {
          filteredObjects.push(object);
        }
      });
      return filteredObjects;
    } else {
      // Only one subunit per object, filter on just that subunit.
      return objectsToFilter.filter(object => {
        return selectedUnitTokens.includes(object.subunit?.token);
      });
    }
  },
  /**
   * Select all of the units listed in the dropdown
   */
  selectAllUnits() {
    const selectedUnits = selectMultipleTokens(this.sortedUnits);
    this.setProperties({
      selectedUnits
    });
  },
  updateSelectedUnits(units) {
    const selectedUnits = selectMultipleTokens(this.sortedUnits, units);
    this.setProperties({
      selectedUnits
    });
  }
});